// Single source of truth for the in-app User Guides page and the
// downloadable Markdown export. Keep content plain so it renders and exports cleanly.

export const GUIDE_META = {
  appName: "Sign-Off",
  tagline: "Charity expense approvals — user guides",
  intro:
    "Sign-Off lets a charity get every expense signed off by 2–6 people, with a tamper-proof audit trail. A submitter uploads receipts and nominates approvers; each approver votes via a secure one-time email link (no account needed); once everyone approves, the receipts and a link to the audit trail are emailed to finance.",
};

export const guides = [
  {
    id: "overview",
    title: "How Sign-Off works",
    audience: "Everyone",
    icon: "BookOpen",
    summary:
      "A one-minute picture of the whole flow, from submitting an expense to the finance hand-off.",
    quickStart: [
      "A submitter creates an expense, uploads up to 5 receipts, and nominates 2–6 approvers.",
      "Each approver gets their own secure, single-use email link — no login required.",
      "Every approver must approve. If any one rejects, the expense is rejected and stops there.",
      "When all approvers sign off, the receipts + a link to the audit trail are emailed to finance.",
      "Every action is written once to an append-only, tamper-proof audit log that anyone can verify.",
    ],
    sections: [
      {
        heading: "The four roles",
        blocks: [
          {
            type: "ul",
            items: [
              "Submitter — a staff member who logs in, creates expenses, and tracks their own sign-off.",
              "Approver — nominated per expense, approves or rejects via a one-time email link. No account.",
              "Admin — creates the organisation, invites members, manages categories, and sees every expense in the org.",
              "Platform super-admin — Sign-Off's operator, who sees activity across all organisations.",
            ],
          },
        ],
      },
      {
        heading: "Key principles",
        blocks: [
          {
            type: "ul",
            items: [
              "Unanimous sign-off: all nominated approvers must approve; a single rejection stops the expense.",
              "One-time links: each approver link works once and is tied to that approver's email.",
              "Tamper-proof: the audit log is append-only and cryptographically chained, so it cannot be edited or deleted without detection.",
              "No payments: Sign-Off records approval only — it never moves money.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "submitter",
    title: "Submitter guide",
    audience: "Staff who submit expenses",
    icon: "FileText",
    summary:
      "Create an expense, attach receipts, nominate approvers, and follow it through to approval.",
    quickStart: [
      "Sign in, then click ‘New expense’.",
      "Enter the payee, amount, and a short description. Pick a category if your charity uses them.",
      "Upload up to 5 receipts/invoices (JPG, PNG, WEBP or PDF, max 10 MB each) — or take a photo on mobile.",
      "Add 2–6 approver email addresses. Optionally save them as a reusable template.",
      "Enter the finance email that should receive the approved package, then click ‘Submit for approval’.",
    ],
    sections: [
      {
        heading: "Getting an account",
        blocks: [
          {
            type: "p",
            text: "If your charity already uses Sign-Off, an admin will invite you by email. Click the invite link, set a password, and you're in. If you're setting the charity up yourself, use ‘Create an organisation’ on the sign-in page — you'll become the first admin.",
          },
        ],
      },
      {
        heading: "Creating an expense",
        blocks: [
          {
            type: "ul",
            items: [
              "Payee / Supplier — who is being paid (e.g. Acme Catering Ltd).",
              "Amount (GBP) — the total on the receipt.",
              "Category (optional) — choose from the categories your admin has set up.",
              "Description — what the expense was for; this helps approvers decide.",
              "Receipts / Invoices — attach up to 5 files. On a phone you can use ‘Take a photo’ to capture a receipt directly.",
            ],
          },
          {
            type: "tip",
            text: "You can attach several files to one expense — useful when a single claim has multiple invoices.",
          },
        ],
      },
      {
        heading: "Choosing approvers",
        blocks: [
          {
            type: "p",
            text: "Add between 2 and 6 approver email addresses. Every one of them must approve for the expense to clear. Approvers don't need a Sign-Off account — they act entirely from the email link.",
          },
          {
            type: "p",
            text: "If you approve the same group often, click ‘Save as template’ to reuse those emails on future expenses. Templates are shared across your organisation.",
          },
        ],
      },
      {
        heading: "Finance & accounts software",
        blocks: [
          {
            type: "ul",
            items: [
              "Send approved invoice to — once everyone signs off, all receipts plus a link to the audit trail are emailed here in one package. You're CC'd.",
              "Submit to accounts software (optional) — for tools like Dext, AutoEntry or Xero. Each receipt is sent as its own separate email (one invoice per email). You trigger this manually with a button on the approved expense.",
            ],
          },
        ],
      },
      {
        heading: "Tracking progress",
        blocks: [
          {
            type: "p",
            text: "Your dashboard shows counts for Awaiting approval, Approved and Rejected. Click any expense to open it and see the receipt viewer, each approver's status, and the full audit log.",
          },
          {
            type: "p",
            text: "You'll receive an email when your expense is submitted, and again when it's fully approved or rejected (a rejection includes the approver's reason if they gave one).",
          },
        ],
      },
    ],
  },
  {
    id: "approver",
    title: "Approver guide",
    audience: "People asked to sign off (no account needed)",
    icon: "CheckCircle2",
    summary:
      "How to review an expense and approve or reject it from your secure email link.",
    quickStart: [
      "Open the email titled ‘Expense approval needed’ and click the review link.",
      "Review the receipts and the expense details (payee, amount, description).",
      "Optionally add a comment — this is recorded in the audit log.",
      "Click ‘Approve’ or ‘Reject’.",
      "That's it — no account or password is required.",
    ],
    sections: [
      {
        heading: "Your secure link",
        blocks: [
          {
            type: "ul",
            items: [
              "The link is unique to you and can be used only once.",
              "It cannot be reused or shared — a used link will not let you vote again.",
              "If you don't recognise an expense, simply ignore the email; no decision is recorded.",
            ],
          },
        ],
      },
      {
        heading: "Reviewing receipts",
        blocks: [
          {
            type: "p",
            text: "The review page shows all attached receipts. Use the thumbnails or arrows to move between them, click an image to open a full-screen view you can zoom into, and view PDFs inline or open them in a new tab.",
          },
        ],
      },
      {
        heading: "Approving or rejecting",
        blocks: [
          {
            type: "ul",
            items: [
              "Approve — records your sign-off. When every approver has approved, the expense is complete.",
              "Reject — stops the expense immediately; no further approvals are recorded. Adding a reason helps the submitter.",
            ],
          },
          {
            type: "tip",
            text: "If you take a while to respond, you may receive a reminder email. Your original link still works.",
          },
        ],
      },
    ],
  },
  {
    id: "admin",
    title: "Admin guide",
    audience: "Organisation administrators",
    icon: "Settings",
    summary:
      "Set up your workspace, invite your team, manage categories, and oversee every expense.",
    quickStart: [
      "Create your organisation from ‘Create an organisation’ on the sign-in page — you become the first admin.",
      "Open ‘Settings’ to rename the organisation and set up expense categories.",
      "Invite colleagues by email; they set their own password from the invite link.",
      "From the dashboard you can see and open every expense in your organisation.",
      "On any expense you can send reminders, download the audit certificate, and export data to CSV.",
    ],
    sections: [
      {
        heading: "Managing your team",
        blocks: [
          {
            type: "ul",
            items: [
              "Invite members — send an email invite from Settings. Pending invites can be revoked at any time.",
              "Promote / demote — switch a member between Admin and Submitter. Admins see all expenses; submitters see only their own.",
            ],
          },
        ],
      },
      {
        heading: "Expense categories",
        blocks: [
          {
            type: "p",
            text: "Add categories in Settings (e.g. ‘Grant A – Youth’, ‘Travel’). Submitters then choose from these when creating an expense, which keeps your reporting tidy. You can add or remove categories at any time.",
          },
        ],
      },
      {
        heading: "Reminders",
        blocks: [
          {
            type: "ul",
            items: [
              "Manual — click ‘Send reminder’ on an expense to re-email only the approvers who haven't responded yet.",
              "Automatic — Sign-Off automatically reminds pending approvers roughly once a day, up to five times each, until they respond.",
            ],
          },
        ],
      },
      {
        heading: "Audit trail & records",
        blocks: [
          {
            type: "ul",
            items: [
              "Audit certificate (PDF) — download a formal certificate of the sign-off history from any expense.",
              "Public audit link — approved expenses have a shareable audit-trail link (finance receives this automatically).",
              "CSV export — export the current filtered dashboard view for your own records or spreadsheets.",
              "Re-send to accounts software — resend the receipts to your accounts inbox if needed, even after approval.",
            ],
          },
        ],
      },
      {
        heading: "Monthly digest",
        blocks: [
          {
            type: "p",
            text: "Admins receive a monthly email digest summarising activity — how many expenses were submitted, approved and rejected, and what's still awaiting sign-off.",
          },
        ],
      },
    ],
  },
  {
    id: "platform",
    title: "Platform super-admin guide",
    audience: "Sign-Off platform operators",
    icon: "Globe",
    summary:
      "See activity across every organisation and keep an eye on new signups.",
    quickStart: [
      "Sign in with a platform-admin account, then open ‘Platform’ in the top navigation.",
      "Review the totals: organisations, users and expenses (with a status breakdown).",
      "Scan the per-organisation table for members and expense consumption.",
      "Use the ‘All users’ table to see every account, its role, organisation and signup date.",
      "Click ‘Export signups’ to download the full signup list as CSV.",
    ],
    sections: [
      {
        heading: "What the back office shows",
        blocks: [
          {
            type: "ul",
            items: [
              "Totals — organisations, users and expenses across the whole platform, plus awaiting/approved/rejected counts.",
              "Organisations — each org's member count, expense count and creation date.",
              "Recent signups — the latest accounts to join.",
              "All users — a full table of every user (name, email, role, organisation, signup date).",
            ],
          },
          {
            type: "p",
            text: "Individual expense details stay private to each charity — the platform view is aggregate only.",
          },
        ],
      },
      {
        heading: "Signup notifications",
        blocks: [
          {
            type: "p",
            text: "Whenever a new organisation signs up, Sign-Off emails the configured platform contact with the new user's name, email, organisation and the time they joined — so you always know when someone new comes on board.",
          },
        ],
      },
      {
        heading: "Access control",
        blocks: [
          {
            type: "p",
            text: "The Platform area is restricted to designated super-admin email addresses. If you can't see the ‘Platform’ link, your account isn't on the platform-admin list.",
          },
        ],
      },
    ],
  },
  {
    id: "finance-audit",
    title: "Finance & audit trail",
    audience: "Finance recipients & anyone verifying a record",
    icon: "ShieldCheck",
    summary:
      "What finance receives on approval, and how anyone can independently verify a record is genuine.",
    quickStart: [
      "When an expense is fully approved, finance receives one email with all receipts attached.",
      "The same email contains a link to the public audit trail — no login needed.",
      "Open the audit trail to see every action and download the PDF audit certificate.",
      "Use the ‘Verify’ page to confirm the record is intact and untampered.",
    ],
    sections: [
      {
        heading: "The finance hand-off",
        blocks: [
          {
            type: "p",
            text: "The approval email is sent to the finance address chosen by the submitter, who is CC'd. It contains the original receipts as attachments and a link to the full audit trail. Sign-Off records that internal sign-off is complete — it does not initiate any payment.",
          },
        ],
      },
      {
        heading: "The audit trail",
        blocks: [
          {
            type: "p",
            text: "The public audit page lists every event in order — created, each approval or rejection, reminders, and the finance hand-off — with who did what and when. You can download a formal PDF audit certificate from this page.",
          },
        ],
      },
      {
        heading: "Verifying integrity",
        blocks: [
          {
            type: "ul",
            items: [
              "Each audit entry is cryptographically chained to the one before it.",
              "The ‘Verify’ page re-checks that chain and confirms nothing has been altered, inserted or removed.",
              "If even a single record had been changed, verification would fail — that's what makes the log tamper-proof.",
            ],
          },
        ],
      },
    ],
  },
];

function blockToMarkdown(block) {
  if (block.type === "p") return `${block.text}\n`;
  if (block.type === "tip") return `> **Tip:** ${block.text}\n`;
  if (block.type === "ul") return block.items.map((i) => `- ${i}`).join("\n") + "\n";
  return "";
}

export function guidesToMarkdown() {
  const lines = [];
  lines.push(`# ${GUIDE_META.appName} — User Guides\n`);
  lines.push(`${GUIDE_META.intro}\n`);
  lines.push(`---\n`);
  for (const g of guides) {
    lines.push(`## ${g.title}`);
    lines.push(`_For: ${g.audience}_\n`);
    lines.push(`${g.summary}\n`);
    lines.push(`### Quick start`);
    g.quickStart.forEach((s, i) => lines.push(`${i + 1}. ${s}`));
    lines.push("");
    for (const sec of g.sections) {
      lines.push(`### ${sec.heading}`);
      for (const b of sec.blocks) lines.push(blockToMarkdown(b));
    }
    lines.push(`---\n`);
  }
  return lines.join("\n");
}
